import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Euro as EuroIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
  Launch as LaunchIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import FollowButton from 'components/common/buttons/FollowButton';


const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
  },
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(2),
}));

// initialSearch: the words searched on the agency list, so its matching programs show first
// onSearchChange(words): the page keeps the words, so the calls list below stays filtered by them
const ProgramSelector = ({ selectedAgency, cooperationType, callStatuses = [], initialSearch = '', onSearchChange, onProgramSelect, onViewAllCalls, onViewAgencies, onPinProgram, pinnedIds = [] }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search words go to the server (?q=), which also looks inside each program's calls
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchTerm.trim().length >= 2 ? searchTerm.trim() : ''), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => { onSearchChange?.(searchQuery); }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  const matchingCalls = programs.reduce((n, p) => n + (p.matched_calls || 0), 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedAgency) {
      fetchPrograms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgency, cooperationType, callStatuses, searchQuery]);

  const fetchPrograms = async () => {
    try {
      if (programs.length) setSearching(true); else setLoading(true);   // keep the list on screen while searching
      const params = new URLSearchParams({ agency: selectedAgency.id, page_size: '500' });
      if (searchQuery) params.set('q', searchQuery);
      if (cooperationType) params.set('cooperation_type', cooperationType);
      // 'statuses' only narrows the call counts; programmes without matching calls stay listed
      if (callStatuses.length > 0) params.set('statuses', callStatuses.join(','));
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'}/funding/funding-programs/?${params}`);
      setPrograms(response.data.results || []);
    } catch (err) {
      // console.log(selectedAgency);
      // console.log(selectedAgency.name);
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const searchBar = (
    <Box display="flex" gap={1.5} alignItems="center" sx={{ mb: 3 }}>
      <TextField
        size="small" fullWidth value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={`Search ${selectedAgency?.name || 'this agency'}'s programs and their calls…`}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          endAdornment: searching ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
        }}
      />
      {searchQuery && matchingCalls > 0 && (
        <Button variant="contained" size="small" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={onViewAllCalls}>
          Show all {matchingCalls.toLocaleString()} matching call{matchingCalls === 1 ? '' : 's'}
        </Button>
      )}
    </Box>
  );

  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'annual':
      case 'biannual':
      case 'quarterly':
        return <ScheduleIcon />;
      case 'continuous':
        return <AccessTimeIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 'continuous': return 'success';
      case 'annual': return 'primary';
      case 'biannual': return 'warning';
      case 'quarterly': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: '85vh' }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: 'primary.50' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Programs from {selectedAgency.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Chip icon={<BusinessIcon />} label={selectedAgency.country} />
              <Chip icon={<AccessTimeIcon />} label={`${programs.length} programs`} />
            </Box>
          </Box>


          <Button
            variant="outlined"
            onClick={onViewAgencies}
                      >
              Back to Agencies
          </Button>
        </Box>
        
        {selectedAgency.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {selectedAgency.description}
          </Typography>
        )}
      </Paper>

      {(programs.length > 0 || searchQuery) && searchBar}

      {programs.length === 0 && searchQuery ? (
        <Alert severity="info">
          No programs of {selectedAgency.name} match “{searchQuery}” in their names, descriptions or calls.
        </Alert>
      ) : programs.length === 0 ? (
        <Alert severity="info">
          {selectedAgency.name}'s programmes aren't listed on Otable yet. You can still view any calls we have
          {selectedAgency.website ? (
            <>, or see their current opportunities on{' '}
              <a href={selectedAgency.website} target="_blank" rel="noopener noreferrer">their website</a>.</>
          ) : '.'}
        </Alert>
      ) : (
        <>

        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}s>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Select a program to view specific funding calls:
          </Typography>
          

          <Button variant="outlined" onClick={onViewAllCalls}>
            View All Calls
          </Button>
        </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: 3,
            }}
          >
            {programs.map((program) => (
              <StyledCard key={program.id} onClick={() => onProgramSelect(program)}>
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Follow / Pin */}
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} mb={1}>
                    <FollowButton
                      contentType="fundingprogram"
                      objectId={program.id}
                      size="small"
                      variant="outlined"
                    />
                    {onPinProgram && (
                      <Tooltip title={pinnedIds.includes(program.id) ? 'Pinned' : 'Pin to Quick Access'}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onPinProgram(program); }}
                        >
                          {pinnedIds.includes(program.id) ? <PinIcon /> : <PinOutlinedIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* Program Name, Abbreviation and Website */}
                  <Box mb={2}>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {program.name}
                      {program.website && (
                        <Tooltip title="Visit Website">
                          <IconButton
                            color="default"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); window.open(program.website, '_blank', 'noopener,noreferrer'); }}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Typography>

                    {program.abbreviation && (
                      <Chip
                        label={program.abbreviation}
                        size="small"
                        color="secondary"
                        sx={{ mb: 1 }}
                      />
                    )}
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, flexGrow: 1, lineHeight: 1.5}}
                  >
                    {program.description}
                  </Typography>



               <Tooltip title={searchQuery ? `${program.active_calls_count || 0} calls in total` : 'Active Calls'}>
                  <Chip
                    label={searchQuery ? `${program.matched_calls || 0} matching calls` : `${program.active_calls_count || 0} Calls`}
                    size="small"
                    variant="outlined"
                    color={(searchQuery ? program.matched_calls : program.active_calls_count) > 0 ? 'success' : 'default'}
                    sx={{width: 'fit-content', mb: 2 }}
                  />
                </Tooltip>


                  {/* Program Details */}
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    {program.budget_range && (
                      <Box display="flex" alignItems="center">
                        <EuroIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {program.budget_range}
                        </Typography>
                      </Box>
                    )}

                    {program.duration_range && (
                      <Box display="flex" alignItems="center">
                        <AccessTimeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Duration: {program.duration_range}
                        </Typography>
                      </Box>
                    )}

                    {program.typical_call_frequency && (
                      <Box display="flex" alignItems="center">
                        {getFrequencyIcon(program.typical_call_frequency)}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          <Chip
                            label={program.frequency_display || program.typical_call_frequency}
                            color={getFrequencyColor(program.typical_call_frequency)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </StyledCard>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProgramSelector;